import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Tooltip,
  Paper,
  Divider,
} from '@mui/material';
import TopBar from '../components/TopBar';
import DownloadIcon from '@mui/icons-material/Download';
import * as htmlToImage from 'html-to-image';

const FONT_MAP = {
  'Gowun Batang': '"Gowun Batang", serif',
  'Nanum Pen Script': '"Nanum Pen Script", cursive',
};

const BACKGROUND_MAP = {
  left: '/images/book-spread-background-left.png',
  center: '/images/book-spread-background.jpg',
  right: '/images/book-spread-background-right.png',
};

const FONT_SIZE_STEP = 2;
const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 24;
const DEFAULT_FONT_SIZE = 16;

const LINE_HEIGHT_MAP = {
  small: 1.8,
  medium: 1.75,
  large: 1.7,
};

// Aspect ratio for different views
const ASPECT_RATIO_MAP = {
  left: '9 / 16',
  center: '16 / 10',
  right: '9 / 16',
};

const alignOptions = [
  { value: 'left', label: '배경 좌측' },
  { value: 'center', label: '양쪽' },
  { value: 'right', label: '우측' },
];

const fontSizeOptions = [
  { value: 'small', label: '글자 작게' },
  { value: 'medium', label: '보통' },
  { value: 'large', label: '크게' },
];

const fontOptions = [
  { value: 'Gowun Batang', label: '폰트 고운바탕' },
  { value: 'Nanum Pen Script', label: '나눔펜' },
];

function HighlightExportPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { highlight, book, authorProfile } = location.state || {};

  // --- STATE ---
  const [backgroundAlign, setBackgroundAlign] = useState('left');
  const [showBookInfo, setShowBookInfo] = useState(true);
  const [showAuthorInfo, setShowAuthorInfo] = useState(true);
  const [font, setFont] = useState('Gowun Batang');
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
  const [isExporting, setIsExporting] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [fontSizeChanging, setFontSizeChanging] = useState(false);

  const exportRef = useRef(null);

  // --- TopBar 스크롤 감지 상태 ---
  const [showTopBar, setShowTopBar] = useState(true);
  const lastScrollY = useRef(window.scrollY);
  const ticking = useRef(false);

  const splitText = useMemo(() => {
    const text = highlight.text || '';
    if (backgroundAlign !== 'center') {
      return { left: text, right: '' };
    }
    const middle = Math.floor(text.length / 2);
    return {
      left: text.substring(0, middle),
      right: text.substring(middle),
    };
  }, [highlight.text, backgroundAlign]);

  // TopBar 스크롤 감지 및 transition
  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentY = window.scrollY;
          if (currentY < 10) {
            setShowTopBar(true);
          } else if (currentY > lastScrollY.current) {
            // 아래로 스크롤
            setShowTopBar(false);
          } else if (currentY < lastScrollY.current) {
            // 위로 스크롤
            setShowTopBar(true);
          }
          lastScrollY.current = currentY;
          ticking.current = false;
        });
        ticking.current = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- HANDLERS ---
  const handleDownload = useCallback(() => {
    if (!exportRef.current) return;
    setIsExporting(true);
    
    htmlToImage.toPng(exportRef.current, {
      quality: 1,
      pixelRatio: 2.5, // Increase pixel ratio for better quality
      backgroundColor: 'transparent',
      fontEmbedCss: `@import url('https://fonts.googleapis.com/css2?family=Gowun+Batang:wght@400;700&family=Nanum+Pen+Script&display=swap');`
    })
    .then((dataUrl) => {
      const link = document.createElement('a');
      link.download = `snaplog-highlight-${new Date().getTime()}.png`;
      link.href = dataUrl;
      link.click();
    })
    .catch((error) => {
      console.error('Image export error:', error);
    })
    .finally(() => {
      setIsExporting(false);
    });
  }, [exportRef]);

  const handleFontSizeChange = useCallback((type) => {
    if (fontSizeChanging) return; // debounce
    let nextSize = fontSize;
    if (type === 'smaller') {
      if (fontSize <= MIN_FONT_SIZE) {
        setToastMsg('더 이상 작아질 수 없습니다');
        return;
      }
      nextSize = Math.max(fontSize - FONT_SIZE_STEP, MIN_FONT_SIZE);
    } else if (type === 'larger') {
      if (fontSize >= MAX_FONT_SIZE) {
        setToastMsg('더 이상 커질 수 없습니다');
        return;
      }
      nextSize = Math.min(fontSize + FONT_SIZE_STEP, MAX_FONT_SIZE);
    }
    setFontSizeChanging(true);
    setFontSize(nextSize);
    setTimeout(() => setFontSizeChanging(false), 200); // 버튼 애니메이션용 debounce
  }, [fontSize, fontSizeChanging]);

  // 토스트 메시지 자동 사라짐
  React.useEffect(() => {
    if (toastMsg) {
      const timer = setTimeout(() => setToastMsg(''), 1200);
      return () => clearTimeout(timer);
    }
  }, [toastMsg]);

  if (!highlight || !book) {
    return (
      <Box>
        <TopBar title="오류" showBack onBack={() => navigate(-1)} />
        <Typography sx={{ p: 2 }}>하이라이트 정보를 불러올 수 없습니다. 이전 페이지로 돌아가 다시 시도해주세요.</Typography>
      </Box>
    );
  }

  const currentFontSize = fontSize + 'px';
  const currentLineHeight = 1.7 + (fontSize - DEFAULT_FONT_SIZE) * 0.03; // px 크기에 따라 약간 보정

  const renderPreview = () => {
    const commonTextStyles = {
      fontFamily: FONT_MAP[font],
      fontSize: currentFontSize,
      lineHeight: currentLineHeight,
      color: '#3A352F',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      textAlign: 'justify',
      transition: 'all 0.3s ease-in-out',
    };

    return (
      <Box
        ref={exportRef}
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          lineHeight: 0,
        }}
      >
        <img
          src={BACKGROUND_MAP[backgroundAlign]}
          alt="Highlight background"
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
        {/* Absolutely positioned content box to match paper texture */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '8%', md: '8%' },
            bottom: { xs: '4%', md: '5%' },
            left: backgroundAlign === 'center' ? { xs: '6%', md: '6%' } : { xs: '10%', md: '10%' },
            right: backgroundAlign === 'center' ? { xs: '6%', md: '6%' } : { xs: '10%', md: '10%' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
          }}
        >
           {backgroundAlign === 'center' ? (
            <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', gap: '12%' }}>
              <Typography sx={{ ...commonTextStyles, width: '50%' }}>
                {splitText.left}
              </Typography>
              <Typography sx={{ ...commonTextStyles, width: '50%' }}>
                {splitText.right}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ ...commonTextStyles, flexGrow: 1, minHeight: 0 }}>
              {splitText.left}
            </Typography>
          )}

          {/* Info Box - Now part of the flex flow */}
          <Box sx={{
            flexShrink: 0,
            pt: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            fontFamily: FONT_MAP[font],
            color: '#3A352F',
          }}>
              {showAuthorInfo && authorProfile ? (
                <Typography sx={{ fontSize: '0.8em', opacity: 0.7 }}>
                  @{authorProfile.displayName || '사용자'}
                </Typography>
              ) : <Box />}
              
              {showBookInfo && (
                <Box sx={{ textAlign: 'right', pl: 1, width: backgroundAlign === 'center' ? '50%' : 'auto' }}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: '0.9em' }}>{book.title}</Typography>
                  <Typography sx={{ fontSize: '0.8em', opacity: 0.8 }}>{book.authors?.join(', ')}</Typography>
                </Box>
              )}
          </Box>
        </Box>
      </Box>
    );
  }

  // prefix 조건부 라벨 생성 함수 추가
  const getPrefixedLabel = (prefix, label, selected, value) => {
    return value === selected ? `${prefix} ${label}` : label;
  };

  // --- ChipButton 개선: 라벨 생성 시 prefix 조건부 적용 ---
  const ChipButton = ({ label, value, selectedValue, onClick, prefix }) => (
    <Box
      onClick={() => onClick(value)}
      sx={{
        px: 1.8, py: 1.1,
        borderRadius: 99,
        fontWeight: 600,
        fontSize: '0.9rem',
        cursor: 'pointer',
        boxShadow: selectedValue === value ? '0 2px 8px 0 rgba(0,0,0,0.10)' : 'none',
        bgcolor: selectedValue === value ? '#111' : '#f5f5f5',
        color: selectedValue === value ? '#fff' : '#888',
        border: selectedValue === value ? '2px solid #111' : '1.5px solid #eee',
        textAlign: 'center',
        userSelect: 'none',
        transition: 'all 0.15s',
        minWidth: 64,
      }}
    >
      {prefix ? getPrefixedLabel(prefix, label, selectedValue, value) : label}
    </Box>
  );

  const FontSizeControl = () => (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
      <Button
        variant="contained"
        size="small"
        onClick={() => handleFontSizeChange('smaller')}
        disabled={fontSize <= MIN_FONT_SIZE || fontSizeChanging}
        sx={{
          minWidth: 0,
          px: 2,
          py: 1,
          fontWeight: 700,
          bgcolor: fontSize <= MIN_FONT_SIZE ? '#eee' : '#222',
          color: fontSize <= MIN_FONT_SIZE ? '#aaa' : '#fff',
          boxShadow: 'none',
          transition: 'all 0.15s',
          transform: fontSizeChanging ? 'scale(0.95)' : 'none',
          '&:hover': { bgcolor: fontSize <= MIN_FONT_SIZE ? '#eee' : '#111' },
        }}
      >
        글자 작게
      </Button>
      <Typography sx={{ fontWeight: 600, fontSize: '1rem', minWidth: 40, textAlign: 'center' }}>{fontSize}px</Typography>
      <Button
        variant="contained"
        size="small"
        onClick={() => handleFontSizeChange('larger')}
        disabled={fontSize >= MAX_FONT_SIZE || fontSizeChanging}
        sx={{
          minWidth: 0,
          px: 2,
          py: 1,
          fontWeight: 700,
          bgcolor: fontSize >= MAX_FONT_SIZE ? '#eee' : '#222',
          color: fontSize >= MAX_FONT_SIZE ? '#aaa' : '#fff',
          boxShadow: 'none',
          transition: 'all 0.15s',
          transform: fontSizeChanging ? 'scale(1.05)' : 'none',
          '&:hover': { bgcolor: fontSize >= MAX_FONT_SIZE ? '#eee' : '#111' },
        }}
      >
        글자 크게
      </Button>
    </Box>
  );

  return (
    <Box>
      {/* TopBar: 고정 해제, 스크롤 방향에 따라 노출/숨김, transition 적용 */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          transition: 'transform 0.35s cubic-bezier(.4,0,.2,1), opacity 0.25s',
          transform: showTopBar ? 'translateY(0)' : 'translateY(-100%)',
          opacity: showTopBar ? 1 : 0,
          background: '#fff',
          boxShadow: showTopBar ? '0 2px 8px 0 rgba(0,0,0,0.04)' : 'none',
        }}
      >
        <TopBar title="이미지로 내보내기" showBack onBack={() => navigate(-1)} />
      </Box>
      {/* Main container for single scroll */}
      <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 12, md: 14 } }}>
        {/* --- Controls Panel --- */}
        <Box 
          sx={{ 
            p: {xs: 0, md: 1},
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2.5, 
            mb: { xs: 3, md: 4 } 
          }}
        >
          {/* 글자 크기 - 최상단으로 이동 */}
          <Box>
            <FontSizeControl />
          </Box>

          {/* 배경 정렬 - 두 번째 */}
          <Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {alignOptions.map(opt => (
                <ChipButton
                  key={opt.value}
                  label={opt.label.replace('배경 ', '')}
                  value={opt.value}
                  selectedValue={backgroundAlign}
                  onClick={setBackgroundAlign}
                  prefix="배경"
                />
              ))}
            </Box>
          </Box>

          {/* 글꼴 - 세 번째 */}
          <Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {fontOptions.map(opt => (
                <ChipButton
                  key={opt.value}
                  label={opt.label.replace('폰트 ', '')}
                  value={opt.value}
                  selectedValue={font}
                  onClick={setFont}
                  prefix="폰트"
                />
              ))}
            </Box>
          </Box>

          {/* 정보 표시 */}
          <Box>
            
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <ChipButton
                label="책 제목 / 저자"
                value="bookInfo"
                selectedValue={showBookInfo ? 'bookInfo' : ''}
                onClick={() => setShowBookInfo(v => !v)}
                prefix={undefined}
              />
              <ChipButton
                label="내 닉네임"
                value="authorInfo"
                selectedValue={showAuthorInfo ? 'authorInfo' : ''}
                onClick={() => setShowAuthorInfo(v => !v)}
                prefix={undefined}
              />
            </Box>
          </Box>
        </Box>
        {/* 토스트 메시지 */}
        {toastMsg && (
          <Box sx={{ position: 'fixed', top: 80, left: 0, right: 0, zIndex: 9999, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
            <Box sx={{ bgcolor: '#222', color: '#fff', px: 3, py: 1.2, borderRadius: 2, fontWeight: 600, fontSize: '1.05rem', boxShadow: '0 2px 12px 0 rgba(0,0,0,0.10)', opacity: 0.97, transition: 'all 0.2s' }}>
              {toastMsg}
            </Box>
          </Box>
        )}
        {/* --- Preview Area --- */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: '500px', pb: { xs: 10, md: 12 } }}>
            {renderPreview()}
          </Box>
        </Box>
      </Box>
      {/* 하단 고정 다운로드 버튼 */}
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 500,
            px: { xs: 2, md: 0 },
            pb: { xs: 2.5, md: 3 },
            pointerEvents: 'auto',
          }}
        >
          <Button 
            fullWidth 
            variant="contained" 
            size="large" 
            onClick={handleDownload} 
            disabled={isExporting} 
            startIcon={isExporting ? <CircularProgress size={22} sx={{ color: '#fff' }}/> : <DownloadIcon />}
            sx={{ 
              bgcolor: '#111', 
              color: '#fff', 
              fontWeight: 700, 
              py: 1.3, 
              fontSize: '1.09rem', 
              borderRadius: 2, 
              boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)',
              textTransform: 'none', 
              '&:hover': { bgcolor: '#222' },
              mb: 0,
            }}
          >
            {isExporting ? '내보내는 중...' : '이미지로 다운로드'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default HighlightExportPage; 